import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandableChips from "components/ExpandableChips";
import IdBadge from "components/IdBadge";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "store/appData";
import {
  DATASET_API,
  type Dataset,
  type DatasetKind,
  type DefectDataset,
} from "./api";
import {
  type ProjectNameDisplay,
  formatProjectLabel,
} from "./hooks/useDatasetViewerSettings";
import t from "./translations";

type DatasetRow = Dataset | DefectDataset;

function isObjectDataset(row: DatasetRow): row is Dataset {
  return "sensitive" in row;
}

interface DatasetGridProps {
  rows: DatasetRow[];
  loading: boolean;
  kind: DatasetKind;
  objectTypeMap: Map<number, string>;
  projectNameDisplay: ProjectNameDisplay;
}

export default function DatasetGrid({
  rows,
  loading,
  kind,
  objectTypeMap,
  projectNameDisplay,
}: DatasetGridProps) {
  const { projects } = useAppData();
  const navigate = useNavigate();
  const showSensitive = kind === "object";
  const detailBasePath = DATASET_API[kind].basePath;
  // 7 shared columns + Modified (both kinds) + Sensitive (object only).
  const columnCount = 8 + (showSensitive ? 1 : 0);

  const projectMap = useMemo(
    () =>
      new Map(
        Object.values(projects).map((p) => [
          p.id,
          formatProjectLabel(p, projectNameDisplay),
        ])
      ),
    [projects, projectNameDisplay]
  );

  const projectChipMax = projectNameDisplay === "id" ? 12 : 2;

  return (
    <TableContainer
      component={Paper}
      sx={{ height: "100%" }}
      data-testid="dataset-table"
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: "fixed",
          "& .MuiTableCell-root": { px: 1 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ width: 64 }}>
              ID
            </TableCell>
            <TableCell sx={{ width: 240 }}>{t.Name}</TableCell>
            <TableCell sx={{ width: 240 }}>{t.TypeList}</TableCell>
            <TableCell sx={{ width: 110 }}>{t.Date}</TableCell>
            {showSensitive && (
              <TableCell align="center" sx={{ width: 90 }}>
                {t.Sensitive}
              </TableCell>
            )}
            <TableCell align="center" sx={{ width: 80 }}>
              {t.ModifiedSinceIntegration}
            </TableCell>
            <TableCell>{t.Whitelist}</TableCell>
            <TableCell>{t.Blacklist}</TableCell>
            <TableCell>{t.SyntheticProjects}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} align="center">
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover
              onClick={() => navigate(`${detailBasePath}/${row.id}`)}
              data-id={row.id}
              data-testid={`datasetGrid.row.${kind}.${row.id}`}
              sx={{ cursor: "pointer" }}
            >
              <TableCell align="center">
                <IdBadge id={row.id} />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {row.name}
                </Typography>
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.type_list}
                  labelMap={objectTypeMap}
                  max={3}
                  chipVariant="outlined"
                  unknownTooltip={t.InaccessibleProject}
                />
              </TableCell>
              <TableCell>
                {row.timestamp
                  ? new Date(row.timestamp).toLocaleDateString()
                  : "—"}
              </TableCell>
              {isObjectDataset(row) && (
                <TableCell align="center">
                  <Checkbox
                    checked={row.sensitive}
                    disabled
                    size="small"
                    sx={{ p: 0 }}
                  />
                </TableCell>
              )}
              <TableCell align="center">
                {row.modified_since_integration && (
                  <Tooltip title={t.ModifiedSinceIntegrationTooltip}>
                    <EditNoteIcon
                      color="warning"
                      fontSize="small"
                      data-testid={`datasetGrid.modified.${row.id}`}
                    />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.approved_customers}
                  labelMap={projectMap}
                  max={projectChipMax}
                  unknownTooltip={t.InaccessibleProject}
                />
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.non_included_customers}
                  labelMap={projectMap}
                  max={projectChipMax}
                  unknownTooltip={t.InaccessibleProject}
                />
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.synthetic_customers}
                  labelMap={projectMap}
                  max={projectChipMax}
                  unknownTooltip={t.InaccessibleProject}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
